import gql from 'graphql-tag';
import React, {useState, useEffect, useContext, useRef} from "react";
import { io } from "socket.io-client";
import "./PWall.scss";
import { useTranslation } from "react-i18next";
import Xbutton from "components/Xbutton";
import { useHistory } from 'react-router-dom';
import PlanToggleWithPayPalButtons from "./PlanToggleWithPayPalButtons";
import PWall_Main from "./PWall_Main";
import PWall_inProgress from "./PWall_inProgress";
import PWall_Success from "./PWall_Success";
import PWallStateManager from "./PWallStateManager";

import UserContext from "../../pages/Login/UserContext";
import { useSubscriptionFunctions } from './SubscriptionFunctions';
import { useUserLogFunctions } from './UserLogFunctions';
import { isUserSubscribed } from '../../utils/subscriptionUtils';
import { useSubscriptionCheck } from '../../utils/hooks/useSubscriptionCheck';
import { trackPurchase } from '../../utils/facebookPixel';

function PWall({isOpen, onExit }) {
  
  {/* ️‍🔥️‍🔥️‍🔥️‍🔥️‍🔥️‍🔥️‍🔥️‍🔥️‍🔥️‍🔥️‍🔥  */}
  const [PWallCheats, setPWallCheats] = useState(false)

  const { t } = useTranslation();
  const history = useHistory();
  // Socket initialization moved to useEffect to prevent leaks
  const {user, handleUserUpdate} = useContext(UserContext);
  const [subscriptionData, setSubscriptionData] = useState(undefined);
  const [PWallFlowState, setPWallFlowState] = useState("main")
  const [PlanSelected, setPlanSelected] = useState("monthly")
  const purchaseTrackedRef = useRef(false);

  // Wrapper function to handle both subscriptionID and planId
  const setSubscriptionID = (subscriptionID, planId) => {
    setSubscriptionData({ subscriptionID, planId });
  };

  // 🔍 STEP 1: PayWall Component Initialization
  useEffect(() => {
    if (isOpen) {
      // debug logs removed
    }
  }, [isOpen, user, PWallFlowState, PlanSelected]);

  const {
    updateUserToSubscribed,
    updateUserToUnsubscribed,
    updateUserSubscriptionID,
  } = useSubscriptionFunctions(handleUserUpdate);

  const { 
    createLogEntry,
    clearUserLog
   } = useUserLogFunctions(handleUserUpdate, user);

  const paywallOpenLoggedRef = useRef(false);

  // Check subscription status
  const { isChecking, isActive } = useSubscriptionCheck(user, handleUserUpdate);

  const gotoLogin = () => {
    history.push('/login');
  }

  const togglePlans = () => {
    switch (PlanSelected){
      case "monthly":
        setPlanSelected("annually")
      break
      case "annually":
        setPlanSelected("monthly")
      break
    }
  }

  useEffect(() => {
    const socket = io("https://felt-webapp.herokuapp.com");
    socket.emit("init", user?.id || "guest");
  
    socket.off("paypalEvent");
    socket.on("paypalEvent", (data) => { // TODO: I commented out the socket call from "paypalWebhook.js" in BE, but this socket still happen, like a ghost socket.
      
      if (data.event_type === "BILLING.SUBSCRIPTION.ACTIVATED") {
        updateUserToSubscribed();
        setPWallFlowState("success");
      }
    });

    // Handle subscription updates
    socket.off("subscriptionUpdate");
    socket.on("subscriptionUpdate", (data) => {
      
      // Check if this update is for the current user
      if (data.userId === user?.id) {
        
        if (data.status === 'ACTIVE' && data.event_type === 'BILLING.SUBSCRIPTION.ACTIVATED') {
          updateUserToSubscribed();
          setPWallFlowState("success");
        }
      } else {
        // not current user
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (subscriptionData) {
      (async () => {
        try {
          setPWallFlowState("processing");
          await updateUserSubscriptionID(subscriptionData.subscriptionID, subscriptionData.planId);
          // Wait for socket/webhook to confirm ACTIVE, then success
        } catch (e) {
          console.error('🔍 STEP 7.1: Failed to persist subscription ID:', e);
        }
      })();
    }
  }, [subscriptionData]);

  // Log paywall open
  useEffect(() => {
    if (isOpen && !paywallOpenLoggedRef.current) {
      paywallOpenLoggedRef.current = true;
      // fire and forget; backend persists in userLog
      createLogEntry({ eventName: 'PAYWALL_OPENED' }).catch(()=>{});
    }
    if (!isOpen) {
      paywallOpenLoggedRef.current = false;
    }
  }, [isOpen]);

  // If the user was a guest during purchase and then logs in / creates an account,
  // re-attach the subscription we captured earlier to the new account.
  useEffect(() => {
    if (subscriptionData && user && !user.isGuest) {
      (async () => {
        try {
          await updateUserSubscriptionID(subscriptionData.subscriptionID, subscriptionData.planId);
        } catch (e) {
          console.error('🔍 PWall: Failed re-applying subscription after auth:', e);
        }
      })();
    }
  }, [user?.isGuest]);

  // Fallback: if user context already reflects subscribed/active while we're processing, move to success
  useEffect(() => {
    if (PWallFlowState === 'processing' && isUserSubscribed(user)) {
      setPWallFlowState('success');
    }
  }, [PWallFlowState, user]);

  const printUser = () => {
    console.log(user);
  }
  
  const logPlanDetails = () => {
    
    if (!user?.subscription) {
      return;
    }
    
    if (!user.subscription.plan_id) {
      return;
    }
    
    // details fetch (logs removed)
    
    // Fetch live PayPal plan details
    fetch(`${process.env.REACT_APP_BACKEND_URL}/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        query: `
          query GetPaypalPlanDetails($planId: String!) {
            getPaypalPlanDetails(planId: $planId)
          }
        `,
        variables: {
          planId: user.subscription.plan_id
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      
      // Extract price and currency from the response
      const planData = data.data?.getPaypalPlanDetails;
      if (planData && planData.billing_cycles && planData.billing_cycles[0]) {
        const pricingScheme = planData.billing_cycles[0].pricing_scheme;
        if (pricingScheme && pricingScheme.fixed_price) {
          const price = pricingScheme.fixed_price.value;
          const currency = pricingScheme.fixed_price.currency_code;
          // values available if needed
        }
      }
    })
    .catch(error => {
      console.error("🔍 DEBUG: Error fetching PayPal plan details:", error);
      console.error("🔍 DEBUG: Error details:", {
        message: error.message,
        stack: error.stack
      });
    });
  };
  
  // Fire Meta Pixel Purchase once upon reaching success
  useEffect(() => {
    if (PWallFlowState !== 'success' || purchaseTrackedRef.current) return;
    purchaseTrackedRef.current = true;
    (async () => {
      let price = 19.99;
      let currency = 'USD';
      try {
        const planId = user?.subscription?.plan_id;
        if (planId) {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/graphql`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              query: `
                query GetPaypalPlanDetails($planId: String!) {
                  getPaypalPlanDetails(planId: $planId)
                }
              `,
              variables: { planId }
            })
          });
          const data = await response.json();
          const planData = data.data?.getPaypalPlanDetails;
          const pricing = planData?.billing_cycles?.[0]?.pricing_scheme?.fixed_price;
          if (pricing?.value) {
            price = parseFloat(pricing.value);
            currency = pricing.currency_code || currency;
          }
        }
      } catch (_) {
        // ignore and use fallbacks
      }
      trackPurchase(price, currency, { method: 'paypal', plan: PlanSelected });
    })();
  }, [PWallFlowState]);

  const ExampleLogEntry = async () => {
    
    // Get the real price from PayPal
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          query: `
            query GetPaypalPlanDetails($planId: String!) {
              getPaypalPlanDetails(planId: $planId)
            }
          `,
          variables: {
            planId: user.subscription.plan_id
          }
        })
      });
      
      const data = await response.json();
      const planData = data.data?.getPaypalPlanDetails;
      let price = 19.99; // fallback
      let currency = 'USD'; // fallback
      
      if (planData && planData.billing_cycles && planData.billing_cycles[0]) {
        const pricingScheme = planData.billing_cycles[0].pricing_scheme;
        if (pricingScheme && pricingScheme.fixed_price) {
          price = parseFloat(pricingScheme.fixed_price.value);
          currency = pricingScheme.fixed_price.currency_code;
        }
      }
      
      await createLogEntry({
        eventName: 'BILLING.SUBSCRIPTION.ACTIVATED',
        // logID!!!!!!!!!!
        details: {
          test: 'true',
          method: 'paypal',
          plan: 'monthly',
          price: price,
          currency: currency,
        },
      });
    } catch (error) {
      console.error("🔍 DEBUG: Error getting real price for log entry:", error);
      // Fallback to hardcoded values if API fails
      await createLogEntry({
        eventName: 'BILLING.SUBSCRIPTION.ACTIVATED',
        details: {
          test: 'true',
          method: 'paypal',
          plan: 'monthly',
          price: 19.99,
          currency: 'USD',
        },
      });
    }
  };

  const testSubscriptionCompletion = () => {
    updateUserToSubscribed();
    setPWallFlowState("success");
  };

  
  return (
    <>
      {PWallCheats && <div className="FLOATING_CHEATS noselect">
        <div className={"label " + (isUserSubscribed(user) ? "label-green" :"label-red")}>{isUserSubscribed(user) ? "SUBSCRIBED" : "NOT SUBSCRIBED"}</div>
        <div className={"label "}>{subscriptionData?.subscriptionID || 'No subscription data'}</div>
        <div className={"cheats-button"} onClick={printUser}>LOG USER</div>
        <div className={"cheats-button"} onClick={logPlanDetails}>📋 Plan Details</div>
        <div className={"cheats-button"} onClick={async () => {
          if (isUserSubscribed(user)) {
            await updateUserToUnsubscribed();
            setSubscriptionData(undefined);
          } else {
            // Generate a fake/placeholder subscription locally for testing UI flows
            const fakeId = `SUB-${Math.random().toString(36).slice(2,10).toUpperCase()}`;
            const fakePlanId = PlanSelected === 'annually' ? 'FAKE-ANNUAL' : 'FAKE-MONTHLY';
            setSubscriptionData({ subscriptionID: fakeId, planId: fakePlanId });
            await updateUserToSubscribed();
          }
        }}>
          {isUserSubscribed(user) ? "🔴 Unsubscribe User" : "🟢 Subscribe User (fake)"}
        </div>
        <div className={"cheats-button"} onClick={()=>ExampleLogEntry()}>logTestEntry</div>
        <div className={"cheats-button"} onClick={()=>clearUserLog()}>Clear user log</div>
        <div className={"cheats-button"} onClick={testSubscriptionCompletion}>Test Subscription Completion</div>
      </div>}
      {isOpen && <div className="PWall_darkBG noselect">
        <PWallStateManager
          flowState={PWallFlowState}
          setFlowState={setPWallFlowState}
          onExit={onExit}
          setSubscriptionID={setSubscriptionID}
        />
        </div>
      }
    </>
  )
}

export default PWall